import "./ObjectList.css";

import { useState, useEffect } from "react";

import { SearchOutlined } from "@ant-design/icons";
import { Button, Input, Tooltip } from "antd";
import ReactPaginate from "react-paginate";

import { WarningIcon } from "../assets/icons";
import { useIsReviewer } from "../context/RoleContext";
import { getValue, recursiveSearch } from "../utils";
import { ReviewerAllowedActions } from "../utils/Constants";

export const ConfirmationDialog = ({ message, onConfirm, onCancel }) => {
  const handleConfirm = (event) => {
    onConfirm({ event });
  };

  const handleCancel = (event) => {
    onCancel(event);
  };

  return (
    <>
      <h1>Are you sure?</h1>
      <p>{message}</p>
      <div className="dialog-buttons">
        <button className="btn cancel" onClick={(event) => handleCancel(event)}>
          Cancel
        </button>
        <button className="btn" onClick={(event) => handleConfirm(event)}>
          Continue
        </button>
      </div>
    </>
  );
};

const ObjectList = ({
  title,
  description,
  tabs,
  columns,
  actions,
  globalActions,
  search,
  data,
  pageSize = 10,
  onAction,
  loading = false,
  hasMore = false,
  onLoadMore = null,
  totalCount = 0,
  useServerPagination = false,
  currentPage = 1,
  onPageChange = null,
  searchTerm = "",
  onSearch = null,
}) => {
  // Reviewers have read-only access: hide every mutating control.
  const isReviewer = useIsReviewer();
  // Reviewers keep only read-only "view" actions; all mutating actions and
  // global "create" actions are hidden for them.
  const visibleActions = isReviewer ? (actions || []).filter((a) => ReviewerAllowedActions.includes(a.name)) : actions;
  const visibleGlobalActions = isReviewer ? (globalActions || []).filter((a) => ReviewerAllowedActions.includes(a.name)) : globalActions;

  const [pageData, setPageData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);
  const [activeTab, setActiveTab] = useState(tabs ? tabs[0].id : null);
  const [searchText, setSearchText] = useState(searchTerm);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Update local search text when external searchTerm changes (e.g., tab change
  // or a deep-link `q` param). For client-side lists, also apply the filter so
  // the rows reflect the seeded term once data has loaded.
  useEffect(() => {
    setSearchText(searchTerm);
    if (!useServerPagination) {
      if (searchTerm && searchTerm.trim()) {
        setFilteredData((data || []).filter((item) => recursiveSearch(item, searchTerm)));
        setItemOffset(0);
      } else {
        setFilteredData(data || []);
      }
    }
  }, [searchTerm, data, useServerPagination]);

  const handleAction = (event, action, confirm, object) => {
    event.preventDefault();

    if (confirm) {
      const handleConfirm = (event) => {
        onAction(event, action, object);
        setShowDialog(false);
        setDialogContent(null);
      };

      setShowDialog(true);
      setDialogContent(
        <ConfirmationDialog title={title} message={confirm} onConfirm={(event) => handleConfirm(event)} onCancel={() => handleCancel()} />
      );
    } else if (onAction) {
      onAction(event, action, object);
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab.id);
  };

  const handleSearch = (text) => {
    setSearchText(text);

    if (!useServerPagination) {
      // Client-side search filters live as the user types.
      let filtered = data.filter((item) => recursiveSearch(item, text));
      setItemOffset(0);
      setFilteredData(filtered);
    }
    // Server-side search is deferred to the "View Results" button / Enter key
    // so it matches the Pending tabs' UX and avoids a request per keystroke.
  };

  const handleViewResults = () => {
    if (useServerPagination && onSearch) {
      onSearch(searchText);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleViewResults();
    }
  };

  const handleCancel = () => {
    setShowDialog(false);
    setDialogContent(null);
  };

  const handlePageClick = (event) => {
    if (useServerPagination) {
      // Server-side pagination - notify parent to fetch new page
      if (onPageChange) {
        onPageChange(event.selected + 1); // react-paginate uses 0-based index
      }
    } else {
      // Client-side pagination
      const newOffset = (event.selected * pageSize) % filteredData.length;
      setItemOffset(newOffset);
    }
  };

  useEffect(() => {
    if (!searchText && data && data.length > 0) setFilteredData(data);

    if (useServerPagination) {
      // For server-side pagination, show current page data
      setPageData(data);
      setPageCount(Math.ceil(totalCount / pageSize));
    } else {
      // Client-side pagination
      const endOffset = itemOffset + pageSize;
      let paginatedData = filteredData.slice(itemOffset, endOffset);
      setPageData(paginatedData);
      setPageCount(Math.ceil(filteredData.length / pageSize));
    }
  }, [itemOffset, pageSize, showDialog, data, filteredData, searchText, useServerPagination, totalCount]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="container">
      {tabs && (
        <div className="tabs">
          {tabs.map((tab) => {
            return (
              <button
                key={tab.id}
                className={tab.id === activeTab ? "active" : ""}
                onClick={() => handleTabClick(tab)}
                style={{ background: "none", border: "none", color: "inherit", cursor: "pointer" }}
              >
                {tab.name}
              </button>
            );
          })}
        </div>
      )}

      <header className="table-header">
        <h1>{title}</h1>
        <h2>{description ? description : "A list of your " + title}</h2>
        <section className="controls">
          {search &&
            (useServerPagination ? (
              // Server-paginated lists (Approved, Add Rules) match the Pending
              // tabs' search UX: styled input + primary "View Results" button.
              // No `search` class here — its CSS (pill radius, ::before icon)
              // would override antd's native styling and break parity.
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginRight: 10 }}>
                <Input
                  placeholder="Search by name, email, address, or KYC ID"
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  style={{ width: 500 }}
                  prefix={<SearchOutlined />}
                  allowClear
                />
                <Button type="primary" onClick={handleViewResults} loading={loading}>
                  View Results
                </Button>
              </div>
            ) : (
              // Client-side lists (Trusted Issuers, Claim Topics) filter live.
              <div className="search">
                <input type="text" placeholder="Search..." value={searchText} onChange={(e) => handleSearch(e.target.value)} />
              </div>
            ))}

          {visibleGlobalActions && (
            <div className="global-actions" style={{ marginLeft: "auto" }}>
              {visibleGlobalActions.map((globalAction) => {
                return (
                  <button
                    key={globalAction.name}
                    className={"btn global-action-" + globalAction.name}
                    onClick={(event) => handleAction(event, globalAction.name, globalAction.confirmation)}
                  >
                    {globalAction.icon ? (
                      <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        {globalAction.icon}
                        {globalAction.label}
                      </span>
                    ) : (
                      globalAction.label
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </section>
      </header>

      {useServerPagination && totalCount > 0 && (
        <div style={{ padding: "0.5rem 0", color: "var(--text-secondary)", fontSize: "0.9rem" }}>
          Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)} - {Math.min(currentPage * pageSize, totalCount)} of {totalCount}{" "}
          {title.toLowerCase()}
        </div>
      )}

      <table>
        <thead>
          <tr>
            {columns.map((column) => {
              let fieldName = column;
              let label = column;
              let style = {};

              if (typeof column === "object") {
                fieldName = column.name;
                label = column.label;
                style = column.width ? { width: column.width } : {};
              }

              return (
                <th key={fieldName} style={style}>
                  {label}
                </th>
              );
            })}
            {visibleActions && visibleActions.length > 0 && <th key={title + "-actions"}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {pageData.map((record) => {
            return (
              <tr key={record.id}>
                {columns.map((column) => {
                  let fieldName = typeof column === "object" ? column.name : column;
                  let key = fieldName + "-" + record.id;
                  if (column.name !== "flagged_account")
                    return <td key={key}>{column.render ? <>{column.render(record)}</> : <>{getValue(fieldName, record)}</>}</td>;
                  else
                    return (
                      <td key={key}>
                        {record.watchlistMatched && (
                          <div className="w-7 text-red-700">
                            <WarningIcon />
                          </div>
                        )}
                        {record.pepMatched && (
                          <div className="w-7 text-red-700">
                            <WarningIcon />
                          </div>
                        )}
                      </td>
                    );
                })}
                {visibleActions && visibleActions.length > 0 && (
                  <td key={"actions" + record.id}>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                      {visibleActions.map((action) => {
                        const buttonContent = (
                          <button
                            key={record.id + "-action-" + action.name}
                            onClick={(event) => handleAction(event, action.name, action.confirmation, record)}
                            style={{
                              color: "var(--link-color)",
                              transition: "0.5s all",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: "0.25rem",
                              display: "inline-flex",
                              alignItems: "center",
                              fontSize: "1.1rem",
                            }}
                            aria-label={action.label}
                          >
                            {action.icon || action.label}
                          </button>
                        );

                        return action.icon ? (
                          <Tooltip key={record.id + "-action-" + action.name} title={action.label} placement="top">
                            {buttonContent}
                          </Tooltip>
                        ) : (
                          buttonContent
                        );
                      })}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {pageData.length === 0 && (isLoading || loading) && (
        <div className="empty">
          <p>Loading {title}...</p>
        </div>
      )}

      {pageData.length === 0 && !isLoading && !loading && (
        <div className="empty">
          <p>No {title.toLowerCase()} found.</p>
        </div>
      )}

      <div className="pagination">
        <ReactPaginate
          breakLabel="&#8230;"
          previousLabel="&#9664;"
          nextLabel="&#9658;"
          onPageChange={handlePageClick}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          renderOnZeroPageCount={null}
          forcePage={useServerPagination ? currentPage - 1 : undefined}
        />
      </div>

      {showDialog && (
        <div className="dialog">
          <div className="dialog-content">{dialogContent}</div>
        </div>
      )}
    </div>
  );
};

export default ObjectList;
